import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  School, Copy, Check, Loader2, PlayCircle, Users, Award, RefreshCw, ExternalLink, ShieldCheck, Sparkles, Zap,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Admin-only Canvas LTI 1.3 demo panel.
 *
 * Shows the config values the instructor pastes into Canvas Developer Keys, and
 * lets us simulate the full Canvas → Lectura launch handshake, roster read
 * (NRPS 2.0) and grade passback (AGS) without needing a real Canvas tenant.
 */
const Copyable = ({ label, value, testid }) => {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed — long-press the field to copy manually.");
    }
  };
  return (
    <div className="min-w-0 rounded-xl border border-border bg-card p-3">
      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
      <div className="mt-1 flex items-center gap-2 min-w-0">
        <code
          data-testid={testid}
          className="flex-1 min-w-0 block truncate rounded-md bg-secondary/60 px-2 py-1.5 text-[11px] font-mono"
          title={value}
        >
          {value || "—"}
        </code>
        <button
          type="button"
          onClick={doCopy}
          className="grid place-items-center h-8 w-8 rounded-md border border-border hover:border-accent/60 transition"
          aria-label={`Copy ${label}`}
        >
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

export const LTIDemo = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simCourse, setSimCourse] = useState("canvas-bio-101");
  const [simRole, setSimRole] = useState("student");
  const [simEmail, setSimEmail] = useState("aria.chen@example.edu");
  const [simName, setSimName] = useState("Aria Chen");
  const [launching, setLaunching] = useState(false);
  const [roster, setRoster] = useState(null);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [scores, setScores] = useState([]);
  const [scoreCtx, setScoreCtx] = useState("canvas-bio-101");
  const [pushingScore, setPushingScore] = useState(false);
  const [launches, setLaunches] = useState([]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    api.get("/lti/config").then((r) => { setConfig(r.data); setLoading(false); })
      .catch(() => { setLoading(false); toast.error("Couldn't load LTI config"); });
    refreshLaunches();
    refreshScores(scoreCtx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const refreshLaunches = () => {
    api.get("/lti/launches/recent").then((r) => setLaunches(r.data.launches || [])).catch(() => {});
  };
  const refreshScores = (ctx) => {
    api.get(`/lti/ags/${ctx}/scores`).then((r) => setScores(r.data.scores || [])).catch(() => setScores([]));
  };

  const loadRoster = async () => {
    setRosterLoading(true);
    try {
      const r = await api.get(`/lti/nrps/context/${simCourse}/memberships`);
      setRoster(r.data);
    } catch (e) {
      toast.error("Couldn't load roster");
    } finally {
      setRosterLoading(false);
    }
  };

  const simulateLaunch = async () => {
    if (launching) return;
    setLaunching(true);
    try {
      const { data } = await api.post("/lti/dev/simulate-launch", {
        course: simCourse,
        role: simRole,
        student_email: simEmail.trim(),
        student_name: simName.trim(),
      });
      // The mock backend hands us a signed id_token — now perform the real LTI
      // POST-form launch the way Canvas would. The endpoint 302s back to /lti/launch
      // and sets the auth cookie in the process, so we submit a native form.
      const form = document.createElement("form");
      form.method = "POST";
      form.action = data.post_url;
      form.target = "_blank"; // open in new tab so the admin doesn't lose their session
      const inp = document.createElement("input");
      inp.type = "hidden"; inp.name = "id_token"; inp.value = data.id_token;
      form.appendChild(inp);
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
      toast.success(`Simulated launch as ${data.role}. Opened in a new tab.`);
      setTimeout(refreshLaunches, 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulation failed");
    } finally {
      setLaunching(false);
    }
  };

  const pushGrade = async () => {
    if (pushingScore) return;
    setPushingScore(true);
    try {
      const { data } = await api.post(`/lti/ags/${scoreCtx}/lineitems/lecture-quiz-1/scores`, {
        userId: `demo-${scoreCtx}-${Math.floor(Math.random() * 900 + 100)}`,
        scoreGiven: Math.floor(65 + Math.random() * 35),
        scoreMaximum: 100,
        activityProgress: "Completed",
        gradingProgress: "FullyGraded",
        comment: "Nice work! Try the focus-mode quiz next.",
      });
      toast.success(`Grade ${data.score}/${data.of} synced to Canvas (mock)`);
      refreshScores(scoreCtx);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grade push failed");
    } finally {
      setPushingScore(false);
    }
  };

  if (!user || user.role !== "admin") {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <p className="text-muted-foreground">Canvas LTI demo is admin-only in mock mode.</p>
      </div>
    );
  }
  if (loading || !config) {
    return <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="lti-demo-page">
      <div>
        <div className="flex items-center gap-3">
          <span className="grid place-items-center h-11 w-11 rounded-2xl bg-accent/15 text-accent">
            <School className="h-6 w-6" />
          </span>
          <div>
            <h1 className="font-serif text-3xl font-bold flex items-center gap-2">
              Canvas LTI 1.3 <span className="rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">MOCK</span>
            </h1>
            <p className="mt-0.5 text-sm text-muted-foreground">Prototype the full Canvas ↔ Lectura handshake. Swap in real Canvas keys to go live.</p>
          </div>
        </div>
      </div>

      {/* Config card */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="h-4 w-4 text-accent" />
          <h2 className="font-serif text-lg font-bold">Developer key config</h2>
          <span className="ml-auto rounded-full bg-secondary px-2.5 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-wider">
            {config.mode}
          </span>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Paste these values into <span className="font-semibold">Canvas Admin → Developer Keys → + LTI Key → Method: Paste JSON</span> (or copy the tool-config URL directly).
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 min-w-0">
          <Copyable label="Issuer" value={config.issuer} testid="lti-issuer" />
          <Copyable label="Client ID" value={config.client_id} testid="lti-client-id" />
          <Copyable label="Deployment ID" value={config.deployment_id} testid="lti-deployment-id" />
          <Copyable label="JWKS URL" value={config.jwks_url} testid="lti-jwks-url" />
          <Copyable label="OIDC Initiation URL" value={config.oidc_initiation_url} testid="lti-login-url" />
          <Copyable label="Target Link URI" value={config.target_link_uri} testid="lti-target-uri" />
          <div className="sm:col-span-2">
            <Copyable label="Tool Config JSON URL" value={config.tool_config_url} testid="lti-tool-config-url" />
          </div>
        </div>
        <a
          href={config.tool_config_url}
          target="_blank"
          rel="noreferrer"
          data-testid="lti-open-tool-config"
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline"
        >
          Open the raw tool-config JSON <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>

      {/* Simulate launch */}
      <div className="rounded-2xl border-2 border-accent/30 bg-accent/5 p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Zap className="h-5 w-5 text-accent" />
          <h2 className="font-serif text-lg font-bold">Simulate a Canvas launch</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          We'll mint a signed RS256 id_token as if Canvas just POSTed it, then run our real <code className="rounded bg-secondary/60 px-1 py-0.5 text-[11px] font-mono">/api/lti/launch</code> flow. Opens in a new tab so your admin session stays intact.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 min-w-0">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Course</label>
            <select data-testid="lti-sim-course" value={simCourse} onChange={(e) => setSimCourse(e.target.value)}
              className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring/60">
              {config.mock_courses.map((c) => (
                <option key={c.context_id} value={c.context_id}>{`${c.context_title} · ${c.context_label}`}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Role</label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {["student", "instructor"].map((r) => (
                <button
                  key={r}
                  type="button"
                  data-testid={`lti-sim-role-${r}`}
                  onClick={() => setSimRole(r)}
                  className={`rounded-xl border-2 px-3 py-2.5 text-sm font-semibold transition ${
                    simRole === r ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground"
                  }`}
                >
                  {r === "instructor" ? "Instructor" : "Student"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Email</label>
            <input data-testid="lti-sim-email" value={simEmail} onChange={(e) => setSimEmail(e.target.value)}
              className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring/60" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Full name</label>
            <input data-testid="lti-sim-name" value={simName} onChange={(e) => setSimName(e.target.value)}
              className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring/60" />
          </div>
        </div>
        <button
          data-testid="lti-simulate-launch-btn"
          onClick={simulateLaunch}
          disabled={launching || !simEmail.trim()}
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-semibold disabled:opacity-60 hover:opacity-90 transition"
        >
          {launching ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing token…</> : <><PlayCircle className="h-5 w-5" /> Simulate Canvas launch</>}
        </button>
      </div>

      {/* Roster (NRPS) */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-accent" />
            <h2 className="font-serif text-lg font-bold">Roster (Names & Roles service · NRPS 2.0)</h2>
          </div>
          <button
            data-testid="lti-load-roster-btn"
            onClick={loadRoster}
            disabled={rosterLoading}
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-border px-4 py-2 text-sm font-semibold hover:border-accent/50 transition"
          >
            {rosterLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Load {simCourse}
          </button>
        </div>
        {roster ? (
          <ul className="divide-y divide-border rounded-xl border border-border" data-testid="lti-roster-list">
            {roster.members.map((m, i) => (
              <li key={m.user_id || i} className="px-4 py-2.5 flex items-center gap-3">
                <div className="grid place-items-center h-9 w-9 rounded-full bg-secondary text-sm font-bold">
                  {(m.name || m.email || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{m.name || m.email?.split("@")[0]}</p>
                  <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-accent">
                  {(m.roles || []).some((r) => r.includes("Instructor")) ? "Instructor" : "Learner"}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-6">Click "Load {simCourse}" to fetch the class roster from the mock LTI NRPS endpoint.</p>
        )}
      </div>

      {/* AGS grade passback */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Award className="h-5 w-5 text-accent" />
            <h2 className="font-serif text-lg font-bold">Grade Passback (AGS)</h2>
          </div>
          <div className="flex items-center gap-2">
            <select value={scoreCtx} onChange={(e) => { setScoreCtx(e.target.value); refreshScores(e.target.value); }}
              className="rounded-full border border-input bg-background px-3 py-1.5 text-xs font-semibold outline-none focus:ring-2 focus:ring-ring/60">
              {config.mock_courses.map((c) => (
                <option key={c.context_id} value={c.context_id}>{c.context_label}</option>
              ))}
            </select>
            <button
              data-testid="lti-push-grade-btn"
              onClick={pushGrade}
              disabled={pushingScore}
              className="inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-4 py-2 text-sm font-semibold hover:opacity-90 transition disabled:opacity-60"
            >
              {pushingScore ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Push a random grade
            </button>
          </div>
        </div>
        {scores.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No grades pushed yet. Try the button above to sync a mock score.</p>
        ) : (
          <ul className="divide-y divide-border rounded-xl border border-border" data-testid="lti-scores-list">
            {scores.slice(0, 10).map((s, i) => (
              <li key={s.id || i} className="px-4 py-2.5 flex items-center gap-3">
                <span className="font-mono text-xs text-muted-foreground w-24 truncate">{s.user_id}</span>
                <span className="flex-1 text-sm font-medium truncate">Lineitem {s.lineitem_id}</span>
                <span className="rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 px-2.5 py-0.5 text-xs font-bold">
                  {Math.round(s.score_given)} / {Math.round(s.score_maximum)}
                </span>
                <span className="text-[10px] text-muted-foreground w-24 text-right hidden sm:block">
                  {new Date(s.created_at).toLocaleTimeString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Recent launches */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <PlayCircle className="h-5 w-5 text-accent" />
            <h2 className="font-serif text-lg font-bold">Recent launches</h2>
          </div>
          <button onClick={refreshLaunches} className="text-xs text-accent hover:underline inline-flex items-center gap-1">
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
        {launches.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No launches yet.</p>
        ) : (
          <ul className="divide-y divide-border rounded-xl border border-border" data-testid="lti-launches-list">
            {launches.slice(0, 8).map((l) => (
              <li key={l.id} className="px-4 py-2.5 flex items-center gap-3">
                <div className="grid place-items-center h-8 w-8 rounded-full bg-accent/15 text-accent text-[11px] font-bold uppercase">
                  {l.role[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{l.email}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {l.context_title || l.context_id} · {l.role}
                  </p>
                </div>
                <span className="text-[10px] text-muted-foreground shrink-0">
                  {new Date(l.created_at).toLocaleTimeString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-center text-xs text-muted-foreground pb-6">
        When you have real Canvas credentials, set <code className="font-mono bg-secondary/60 px-1 py-0.5 rounded">LTI_ISSUER</code> and <code className="font-mono bg-secondary/60 px-1 py-0.5 rounded">LTI_CLIENT_ID</code> in the backend .env and the same UI switches to production mode — no code changes needed.
      </p>
    </div>
  );
};

export default LTIDemo;
